import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { ProfilePictureCaptureDialog } from '@/components/ProfilePictureCaptureDialog/ProfilePictureCaptureDialog'
import { ProfilePicturePreview } from '@/components/ProfilePictureCaptureDialog/ProfilePicturePreview'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  useUpdateProfileMutation,
  useUserInfoQuery,
} from '@/store/api/Users/Users.query'
import { trackOnboardingStepViewed, OnboardingSteps } from '@/utils/analytics'
import { buildFormDataWithImage } from '@/utils/formDataBuilder'
import type { updateProfileSchemaType } from '@/utils/schemas'
import { updateProfileSchema } from '@/utils/schemas'

export const SetPersonalInfo = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const hasTrackedView = useRef(false)

  const [profilePic, setProfilePic] = useState<File | undefined>(undefined)
  const [displayImageUrl, setDisplayImageUrl] = useState<string>('')

  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation()

  const { data: userData, isLoading: isLoadingUser } = useUserInfoQuery()
  const hasProfilePic = !!userData?.profilePicUrl

  // Track step viewed once on mount
  useEffect(() => {
    if (!hasTrackedView.current) {
      trackOnboardingStepViewed(OnboardingSteps.SET_PERSONAL_INFO)
      hasTrackedView.current = true
    }
  }, [])

  const form = useForm<updateProfileSchemaType>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      firstname: userData?.firstname || '',
      lastname: userData?.lastname || '',
    },
  })

  useEffect(() => {
    if (userData) {
      form.reset({
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
      })
      if (userData.profilePicUrl) {
        setDisplayImageUrl(userData.profilePicUrl)
      }
    }
  }, [userData, form])

  const { handleSubmit, control } = form

  const handleImageChange = (file: File | undefined) => {
    if (file) {
      setProfilePic(file)
      const blobUrl = URL.createObjectURL(file)
      setDisplayImageUrl(blobUrl)
    }
  }

  const handleUpdateProfile = async (data: updateProfileSchemaType) => {
    try {
      const formData = buildFormDataWithImage({
        selectedFile: profilePic,
        data: {
          firstname: data.firstname,
          lastname: data.lastname,
        },
        imageFieldName: 'profilePic',
        dataFieldName: 'data',
        filterData: false,
      })

      await updateProfile(formData).unwrap()

      navigate(AppRoutes.setupDigitalFootprint)
    } catch (error) {
      toast.error(t('feedback.toast.errorSavingChanges'))
      catchWithSentry(error)
    }
  }

  return (
    <WeScreen useHeader={false} isLoading={isLoadingUser}>
      <div className="flex flex-col gap-8 w-full h-full sm:justify-center items-center">
        <div className="flex flex-col gap-3 w-1/2 max-lg:w-full">
          <p className="text-[40px] max-sm:text-4xl font-medium text-neutralColor80 text-center">
            {t('signUp.updateProfile.title')}
          </p>
          <p className="text-neutralColor80 text-2xl max-sm:text-xl font-medium text-center">
            {t('signUp.updateProfile.subtitle')}
          </p>
        </div>

        <div className="flex flex-col gap-8 items-center w-1/2 max-lg:w-full">
          <div className="flex flex-col gap-5 items-center w-full bg-baseColorHighlight rounded-3xl p-6">
            {/* Photo Selector */}
            <div className="flex flex-col gap-4 justify-center items-center w-full">
              <ProfilePicturePreview imageUrl={displayImageUrl} size="medium" />
              {!hasProfilePic ? (
                <ProfilePictureCaptureDialog
                  onImageChange={handleImageChange}
                  trigger={
                    <Button
                      variant="outline"
                      className="rounded-xl h-10 border-neutralColor10 font-bold"
                    >
                      {displayImageUrl
                        ? t('signUp.updateProfile.changePhoto')
                        : t('signUp.updateProfile.addPhoto')}
                    </Button>
                  }
                />
              ) : (
                <div className="h-10 flex items-center">
                  <p className="text-neutralColor60 text-sm font-medium italic">
                    {t('signUp.updateProfile.photoLocked')}
                  </p>
                </div>
              )}
              <p className="text-neutralColor80 text-sm font-normal">
                {!hasProfilePic
                  ? displayImageUrl
                    ? t('signUp.updateProfile.descriptionWithImage')
                    : t('signUp.updateProfile.description')
                  : null}
              </p>
            </div>
            <div className="w-full h-[1px] bg-neutralColor5" />

            {/* Form */}
            <Form {...form}>
              <form className="w-full flex flex-col gap-8 items-center">
                <div className="flex gap-4 rounded-3xl w-full max-md:flex-col">
                  <FormField
                    control={control}
                    name="firstname"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel className="text-neutralColor text-sm font-normal">
                          {t('signUp.updateProfile.firstname')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            className="rounded-3xl"
                            {...field}
                            placeholder={t(
                              'signUp.updateProfile.firstnamePlaceholder',
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="lastname"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel className="text-neutralColor text-sm font-normal">
                          {t('signUp.updateProfile.lastname')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            className="rounded-3xl"
                            {...field}
                            placeholder={t(
                              'signUp.updateProfile.lastnamePlaceholder',
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </form>
            </Form>
          </div>
        </div>
        <div className="flex gap-6 items-center">
          <Button variant="outline">{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit(handleUpdateProfile)}
            className="text-base font-bold"
            isLoading={isUpdating}
            disabled={
              !form.formState.isValid || (!hasProfilePic && !profilePic)
            }
          >
            {t('common.continue')}
          </Button>
        </div>
      </div>
    </WeScreen>
  )
}
